// The Apogee scoring pipeline (docs/02-Game-Design/Scoring.md and
// Alignments.md). Integer arithmetic throughout, rounding half up at the end
// of each step, so browser and Worker agree to the point. The steps, per
// constellation then per play:
//   1. star value: (a) base-value sets, (b) base-value additions, (c) star
//      percentages added together (the loop round a surrounded planet is
//      +100, or what an alignment says instead);
//   2. points added to the constellation;
//   3. constellation percentages added together (the shape bonus and the
//      alignments');
//   4. the standings: effects that depend on the other constellations' scores,
//      from a snapshot of every step-3 score;
//   5. points added to the play itself;
//   6. a percentage on the whole total.
// Every alignment plugs into the step it belongs to (alignments/types.ts).
import { alignmentName, type AlignmentId } from '../alignments/catalogue';
import { buildContext, withoutPlanet } from '../alignments/context';
import { alignmentDef, defsInForce } from '../alignments/registry';
import type { AlignmentContext, ConstellationCtx, ScoreInput, StandingEffect, StandingEntry, StarCtx } from '../alignments/types';
import type { PlanetKind } from '../level/presets';
import { planetsOf, type Planet, type PlayLevel, type StarNode } from '../level/schema';
import { nodeMap } from '../play/state';
import { treeLines } from '../play/tree';
import type { Constellation } from '../play/types';
import { letterValue } from './letterValues';
import { shapeBonusPercent, shapeMetrics, type ShapeMetrics } from './shape';

/** The stars on the loop round a surrounded planet score this much more, unless an alignment says otherwise. */
export const LOOP_PERCENT = 100;

/** `base` with `percent` on top, rounded half up. */
export function withPercent(base: number, percent: number): number {
  return Math.floor((base * (100 + percent) + 50) / 100);
}

export interface StarScore {
  id: number;
  letter: string;
  /** The base value after the sets and additions: what the star prints. */
  base: number;
  /** The base value is not the letter's own. */
  changed: boolean;
  /** The percentages on the star, added together. */
  percent: number;
  value: number;
}

export interface StandingScore {
  percent: number;
  zeroed: boolean;
  /** The word whose step-3 score replaced this one's. */
  copiedFrom: string | null;
}

export interface WordScore {
  word: string;
  stars: StarScore[];
  /** Σ star values: the constellation after step 1. */
  letters: number;
  /** Points added to the constellation (step 2). */
  additions: number;
  shape: ShapeMetrics;
  shapeBonusPercent: number;
  /** The percentages on the constellation, the shape bonus included (step 3). */
  percent: number;
  /** The constellation surrounds a planet. */
  planet: boolean;
  /** After step 3: what the standings see. */
  subtotal: number;
  standing: StandingScore | null;
  total: number;
}

export interface PlayAdditionScore {
  alignment: AlignmentId;
  label: string;
  count: number;
  each: number;
  total: number;
}

/** A chosen alignment as the play shows it. */
export interface AlignmentStatus {
  planet: Planet;
  alignment: AlignmentId;
  name: string;
  /** In force: a constellation surrounds the planet right now. */
  active: boolean;
  /** The constellation surrounding the planet, when one does. */
  word: string | null;
  /** What the alignment adds to the running score right now; 0 while out of force. */
  points: number;
  /** The rule as the player reads it, with its live figure; shown in force or not. */
  text: string;
}

export interface PlayScore {
  words: WordScore[];
  playAdditions: PlayAdditionScore[];
  /** Σ word totals and play additions, before the final percentage. */
  subtotal: number;
  finalPercent: number;
  /** The score during play, and the level score once finished. */
  running: number;
  /** The chosen alignments, in the order chosen. */
  alignments: AlignmentStatus[];
}

const EMPTY: ScoreInput = { constellations: [] };

interface Run {
  words: WordScore[];
  playAdditions: PlayAdditionScore[];
  subtotal: number;
  finalPercent: number;
  running: number;
}

function run(level: PlayLevel, constellations: readonly Constellation[], ctx: AlignmentContext): Run {
  const defs = defsInForce(ctx);
  const nodes = nodeMap(level);
  const planets = new Map(planetsOf(level).map((p) => [p.id, p]));
  let loopPercent = LOOP_PERCENT;
  let extraEndpoints = 0;
  for (const def of defs) {
    if (def.loopPercent !== undefined) loopPercent = Math.max(loopPercent, def.loopPercent);
    extraEndpoints += def.extraEndpoints ?? 0;
  }
  const cctxs: ConstellationCtx[] = constellations.map((c, index) => ({
    index,
    constellation: c,
    word: c.word,
    nodes: c.nodes.map((id) => nodes.get(id)).filter((n): n is StarNode => n !== undefined && n.type === 'star'),
    planet: c.planet ? (planets.get(c.planet.id) ?? null) : null,
    shape: shapeMetrics(c.nodes, treeLines(c), extraEndpoints),
  }));

  // Steps 1 to 3, per constellation.
  const words: WordScore[] = cctxs.map((cc) => {
    const loop = new Set(cc.constellation.planet?.loop ?? []);
    const stars: StarScore[] = cc.nodes.map((node, index) => {
      const lv = letterValue(level.lang, node.letter);
      const star: StarCtx = { node, index, onLoop: loop.has(node.id), letterValue: lv, base: lv };
      for (const def of defs) {
        const set = def.baseSet?.(star, cc, ctx);
        if (set !== undefined) star.base = set;
      }
      let add = 0;
      for (const def of defs) add += def.baseAdd?.(star, cc, ctx) ?? 0;
      star.base += add;
      let percent = star.onLoop ? loopPercent : 0;
      for (const def of defs) percent += def.starPercent?.(star, cc, ctx) ?? 0;
      return { id: node.id, letter: node.letter, base: star.base, changed: star.base !== lv, percent, value: withPercent(star.base, percent) };
    });
    const letters = stars.reduce((sum, s) => sum + s.value, 0);
    let additions = 0;
    for (const def of defs) additions += def.wordAdd?.(cc, ctx) ?? 0;
    const shapeBonus = shapeBonusPercent(cc.shape.value);
    let percent = shapeBonus;
    for (const def of defs) percent += def.wordPercent?.(cc, ctx) ?? 0;
    const subtotal = withPercent(letters + additions, percent);
    return { word: cc.word, stars, letters, additions, shape: cc.shape, shapeBonusPercent: shapeBonus, percent, planet: cc.constellation.planet !== undefined, subtotal, standing: null, total: subtotal };
  });

  // Step 4: the standings, from a snapshot of the step-3 scores.
  const entries: StandingEntry[] = words.map((w, index) => ({ index, ctx: cctxs[index]!, score: w.subtotal }));
  const effects: StandingEffect[] = [];
  for (const def of defs) if (def.standing) effects.push(...def.standing(entries, ctx));
  if (effects.length > 0) {
    const percents = new Map<number, number>();
    const zeroed = new Set<number>();
    const copies = new Map<number, number>();
    for (const e of effects) {
      if (e.percent) percents.set(e.index, (percents.get(e.index) ?? 0) + e.percent);
      if (e.zero) zeroed.add(e.index);
      if (e.copyOf !== undefined) copies.set(e.index, e.copyOf);
    }
    words.forEach((w, i) => {
      const percent = percents.get(i) ?? 0;
      const zero = zeroed.has(i);
      const copyOf = copies.get(i);
      if (!percent && !zero && copyOf === undefined) return;
      let total = withPercent(w.subtotal, percent);
      if (zero) total = 0;
      if (copyOf !== undefined) total = entries[copyOf]?.score ?? total;
      w.standing = { percent, zeroed: zero, copiedFrom: copyOf !== undefined ? (words[copyOf]?.word ?? null) : null };
      w.total = total;
    });
  }

  // Step 5: points added to the play.
  const playAdditions: PlayAdditionScore[] = [];
  for (const def of defs) {
    const add = def.playAdd?.(ctx);
    if (add && add.count > 0) playAdditions.push({ alignment: def.id, label: add.label, count: add.count, each: add.each, total: add.count * add.each });
  }
  const subtotal = words.reduce((sum, w) => sum + w.total, 0) + playAdditions.reduce((sum, a) => sum + a.total, 0);

  // Step 6: the final percentage.
  let finalPercent = 0;
  for (const def of defs) finalPercent += def.finalPercent?.(ctx) ?? 0;
  return { words, playAdditions, subtotal, finalPercent, running: withPercent(subtotal, finalPercent) };
}

/** Score a play: its constellations under the alignments in force. */
export function scorePlay(level: PlayLevel, input: ScoreInput): PlayScore {
  const ctx = buildContext(level, input);
  const full = run(level, input.constellations, ctx);
  const alignments: AlignmentStatus[] = (input.alignments ?? []).map((choice) => {
    const planet = planetsOf(level).find((p) => p.id === choice.planet)!;
    const active = ctx.active.get(choice.planet);
    const points = active ? full.running - run(level, input.constellations, withoutPlanet(ctx, choice.planet)).running : 0;
    return {
      planet,
      alignment: choice.alignment,
      name: alignmentName(choice.alignment),
      active: active !== undefined,
      word: active?.word ?? null,
      points,
      // The rule reads the same while the planet is free again, so the player still sees what it would do.
      text: alignmentDef(choice.alignment).text(ctx),
    };
  });
  return { ...full, alignments };
}

/** One constellation's score, as the last word of `input` plus it: on an empty play, on its own. */
export function scoreConstellation(level: PlayLevel, c: Constellation, input: ScoreInput = EMPTY): WordScore {
  const play = scorePlay(level, { ...input, constellations: [...input.constellations, c] });
  return play.words[play.words.length - 1]!;
}

/** What adding a constellation to the play would do to the running score: the preview of a word in progress. */
export function scoreDelta(level: PlayLevel, input: ScoreInput, c: Constellation): number {
  return scorePlay(level, { ...input, constellations: [...input.constellations, c] }).running - scorePlay(level, input).running;
}

/** A star's printed base value under the alignments in force, and which planet's alignment changed it, if one did. */
export interface PrintedValue {
  base: number;
  changed: boolean;
  by: PlanetKind | null;
}

/**
 * What every star prints (Apogee's values under the stars): its base value
 * after the sets and additions of the alignments in force, as it would be
 * in a constellation of its own. The rules about "this constellation"
 * (Majesty) reach no free star.
 */
export function printedValues(level: PlayLevel, input: ScoreInput): Map<number, PrintedValue> {
  const ctx = buildContext(level, input);
  const defs = defsInForce(ctx);
  const out = new Map<number, PrintedValue>();
  for (const node of level.nodes) {
    const lv = letterValue(level.lang, node.letter);
    if (defs.length === 0) {
      out.set(node.id, { base: lv, changed: false, by: null });
      continue;
    }
    const cc: ConstellationCtx = { index: -1, constellation: { word: node.letter, nodes: [node.id], lines: [] }, word: node.letter, nodes: [node], planet: null, shape: { span: 0, endpoints: 0, value: 0 } };
    const star: StarCtx = { node, index: 0, onLoop: false, letterValue: lv, base: lv };
    let by: PlanetKind | null = null;
    for (const def of defs) {
      const set = def.baseSet?.(star, cc, ctx);
      if (set !== undefined && set !== star.base) {
        star.base = set;
        by = def.planet;
      }
    }
    for (const def of defs) {
      const add = def.baseAdd?.(star, cc, ctx) ?? 0;
      if (add !== 0) {
        star.base += add;
        by = by ?? def.planet;
      }
    }
    out.set(node.id, { base: star.base, changed: star.base !== lv, by: star.base !== lv ? by : null });
  }
  return out;
}

/** An alignment's rule as the player reads it now: with its live figure under this play. */
export function alignmentText(level: PlayLevel, input: ScoreInput, id: AlignmentId): string {
  const def = alignmentDef(id);
  return def.text(buildContext(level, input));
}
